import { useState, useEffect } from 'react';
import { useQuery, QueryObserverResult } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  QueryConstraint,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

type FirestoreOperador =
  '==' | '!=' | '<' | '<=' | '>' | '>=' | 'array-contains' | 'array-contains-any' | 'in' | 'not-in';

interface FirestoreFiltro {
  campo: string;
  operador: FirestoreOperador;
  valor: any;
}

interface FirestoreOrdenacao {
  campo: string;
  direcao: 'asc' | 'desc';
}

interface CamposPermitidos {
  [colecao: string]: string[];
}

const CAMPOS_PERMITIDOS: CamposPermitidos = {
  transacoes: [
    'userId', 'status', 'data_hora', 'valor', 'tipo_operacao', 
    'unidade_id', 'timestamp', 'validated', 'merchantInfo',
    'cliente_id', 'tipo', 'id'
  ],
  clientes: [
    'id', 'nome', 'email', 'telefone', 'endereco', 'data_cadastro',
    'status', 'userId',
  ],
  usuarios: [
    'id', 'nome', 'email', 'tipo', 'status', 'data_cadastro'
  ]
};

function validarCampo(colecao: string, campo: string): boolean {
  const camposUniversais = ['id', '__name__'];
  
  if (camposUniversais.includes(campo)) {
    return true;
  }

  const camposPermitidos = CAMPOS_PERMITIDOS[colecao];
  
  if (!camposPermitidos) {
    console.warn(`Coleção '${colecao}' não está na lista de campos permitidos.`);
    const camposProibidos = ['senha', 'password', 'token', 'chavePrivada', '_secret'];
    if (camposProibidos.includes(campo.toLowerCase())) {
      return false;
    }
    return true;
  }

  return camposPermitidos.includes(campo);
}

function useFirestoreQuery<T extends DocumentData>(
  colecao: string,
  filtros?: FirestoreFiltro[] | null,
  ordenacao?: FirestoreOrdenacao | null,
  limite?: number | null,
  dependenciasQueryKey?: any[]
) {
  const queryKey = [
    'firestore', 
    colecao, 
    filtros, 
    ordenacao, 
    limite, 
    ...(Array.isArray(dependenciasQueryKey) ? dependenciasQueryKey : [])
  ];

  const {
    data: dados,
    isLoading: carregando,
    isError: isErro,
    error: erro,
    refetch,
  } = useQuery<T[], Error>({
    queryKey: queryKey,
    queryFn: async () => {
      try {
        if (!colecao || typeof colecao !== 'string' || colecao.trim() === '') {
          throw new Error('Nome da coleção inválido');
        }

        const colecoesRestritas = ['configuracoes', 'tokens', 'logsSistema', 'backups', 'chaves', 'apiKeys'];
        if (colecoesRestritas.includes(colecao)) {
          throw new Error(`Acesso à coleção ${colecao} não permitido`);
        }

        const colecaoRef = collection(db, colecao);
        
        if (!colecaoRef) {
          throw new Error(`Falha ao criar referência para a coleção: ${colecao}`);
        }

        const restricoesQuery: QueryConstraint[] = [];

        // Aplica filtros
        if (filtros && filtros.length > 0) {
          filtros.forEach((filtro) => {
            if (!filtro.campo || !filtro.operador) {
              throw new Error('Filtro inválido: campo ou operador não definido');
            }

            if (!validarCampo(colecao, filtro.campo)) {
              console.warn(`Campo '${filtro.campo}' não está na lista permitida para a coleção ${colecao}`);
              
              const camposSensiveis = ['senha', 'password', 'token', 'chavePrivada', '_secret'];
              if (camposSensiveis.includes(filtro.campo.toLowerCase())) {
                throw new Error(`Filtro por campo sensível '${filtro.campo}' não permitido`);
              }
            }

            restricoesQuery.push(where(filtro.campo, filtro.operador, filtro.valor));
          });
        }

        // Aplica ordenação
        if (ordenacao && ordenacao.campo && ordenacao.campo.trim() !== '') {
          restricoesQuery.push(orderBy(ordenacao.campo, ordenacao.direcao || 'asc'));
        }

        // Aplica limite
        if (limite) {
          const limiteMaximo = 1000;
          const limiteFinal = Math.min(limite, limiteMaximo);
          if (limiteFinal > 0) {
            restricoesQuery.push(limit(limiteFinal));
          }
        }

        let consultaFinal;
        let querySnapshot;
        
        try {
          // Primeira tentativa: query completa
          if (restricoesQuery.length > 0) {
            consultaFinal = query(colecaoRef, ...restricoesQuery);
          } else {
            consultaFinal = query(colecaoRef);
          }
          
          querySnapshot = await getDocs(consultaFinal);
          
        } catch (error) {
          // SE HOUVER ERRO DE ÍNDICE: tenta uma query mais simples
          if (error instanceof Error && error.message.includes('index')) {
            console.warn('Erro de índice detectado, tentando query alternativa...');
            
            // Query alternativa sem ordenação complexa
            const restricoesSimples: QueryConstraint[] = [];
            
            // Mantém apenas filtros WHERE (remove ORDER BY)
            if (filtros && filtros.length > 0) {
              filtros.forEach((filtro) => {
                restricoesSimples.push(where(filtro.campo, filtro.operador, filtro.valor));
              });
            }
            
            // Aplica limite se existir
            if (limite) {
              const limiteFinal = Math.min(limite, 1000);
              restricoesSimples.push(limit(limiteFinal));
            }
            
            if (restricoesSimples.length > 0) {
              consultaFinal = query(colecaoRef, ...restricoesSimples);
            } else {
              consultaFinal = query(colecaoRef);
            }
            
            querySnapshot = await getDocs(consultaFinal);
            
            // Se a query alternativa funcionar, avisa sobre a necessidade do índice
            console.warn('Query alternativa executada com sucesso. Crie o índice para melhor performance.');
            
          } else {
            throw error;
          }
        }

        const documentos = querySnapshot.docs;
        if (documentos.length > 1000) {
          console.warn('Consulta retornou muitos documentos:', documentos.length);
        }

        const resultados: T[] = documentos.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        } as any)) as T[];

        return resultados;

      } catch (error) {
        console.error('Erro detalhado na query do Firestore:', error);
        
        if (error instanceof Error && error.message.includes('index')) {
          const mensagemErro = process.env.NODE_ENV === 'development' 
            ? `Erro de índice: ${error.message}. É necessário criar um índice composto no Firebase.`
            : 'Erro de configuração do banco de dados. Contate o administrador.';
          
          throw new Error(mensagemErro);
        }
        
        const mensagemErro = process.env.NODE_ENV === 'development' 
          ? `Falha ao buscar dados: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
          : 'Erro ao carregar dados. Tente novamente.';
        
        throw new Error(mensagemErro);
      }
    },
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
    enabled: true,
    retry: (failureCount, error) => {
      if (error.message.includes('não permitido') || error.message.includes('índice')) {
        return false;
      }
      return failureCount < 2;
    },
  });

  return {
    dados: dados || [], // GARANTIR QUE SEMPRE RETORNE UM ARRAY
    carregando,
    erro: isErro ? (erro as Error) : null,
    refetch,
  };
}

export { useFirestoreQuery, type FirestoreFiltro, type FirestoreOrdenacao, type FirestoreOperador };